<template>
  <main class="support-review">
    <header class="review-header">
      <div>
        <p class="eyebrow">Lynda support</p>
        <h1>Review and move on</h1>
      </div>
      <div class="health">
        <span :class="['health-dot', loading ? 'loading' : '']"></span>
        {{ loading ? 'Updating…' : `${activeCount} remaining` }}
      </div>
    </header>

    <section class="toolbar">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        :class="['tab', { active: queue === tab.key }]"
        @click="selectQueue(tab.key)"
      >
        {{ tab.label }} <strong>{{ counts[tab.key] || 0 }}</strong>
      </button>
      <input v-model.trim="search" type="search" placeholder="Search name, email or order…" @input="ensureSelection">
    </section>

    <section v-if="groupOptions.length" class="groups" aria-label="Filter similar replies">
      <button :class="{ active: !groupKey }" @click="selectGroup('')">All</button>
      <button
        v-for="group in groupOptions"
        :key="group.key"
        :class="{ active: groupKey === group.key }"
        @click="selectGroup(group.key)"
      >
        {{ group.label }} <strong>{{ group.count }}</strong>
      </button>
    </section>

    <p v-if="error" class="error-banner">{{ error }}</p>

    <section class="review-grid">
      <aside class="queue" aria-label="Support queue">
        <button
          v-for="item in filteredItems"
          :key="item.thread_id"
          :class="['queue-item', { selected: item.thread_id === selectedId }]"
          @click="selectItem(item.thread_id)"
        >
          <span class="queue-name">{{ item.customer_name || item.customer_email }}</span>
          <span class="queue-subject">{{ item.subject }}</span>
          <span class="queue-meta">
            {{ item.university_slug || 'University unknown' }}
            <em v-if="item.feedback_outcome === 'wrong_answer'">Wrong answer</em>
            <em v-else-if="item.requires_human">Check</em>
          </span>
        </button>
        <p v-if="!filteredItems.length && !loading" class="empty">Nothing in this queue.</p>
      </aside>

      <article class="case" v-if="selected">
        <div class="case-heading">
          <div>
            <p class="eyebrow">Customer</p>
            <h2>{{ selected.customer_name || selected.customer_email }}</h2>
            <p>{{ selected.customer_email }}</p>
          </div>
          <span v-if="selected.requires_human" class="warning">Human check</span>
        </div>

        <section class="case-section">
          <h3>Customer asked</h3>
          <pre>{{ detailLoading ? 'Loading email…' : (detail.customer_message || 'Email unavailable') }}</pre>
        </section>

        <section class="case-section evidence">
          <h3>Matched evidence</h3>
          <dl>
            <div><dt>Match</dt><dd>{{ selected.match_status || 'unknown' }}</dd></div>
            <div><dt>University</dt><dd>{{ selected.university_slug || 'Not confirmed' }}</dd></div>
            <div><dt>Registration</dt><dd>{{ registrationLabel }}</dd></div>
            <div><dt>Lynda recommends</dt><dd>{{ actionLabel(selected.recommended_action) }}</dd></div>
          </dl>
          <p v-if="selected.decision_reason" class="reason">{{ selected.decision_reason }}</p>
          <div class="evidence-links">
            <a :href="selected.gmail_thread_url" target="_blank" rel="noopener">Open Gmail</a>
            <a
              v-for="link in selected.verified_links || []"
              :key="link.url"
              :href="link.url"
              target="_blank"
              rel="noopener"
            >{{ linkLabel(link.kind) }}</a>
          </div>
        </section>
      </article>

      <article class="draft" v-if="selected">
        <div class="draft-heading">
          <div>
            <p class="eyebrow">Exact Gmail draft</p>
            <h2>{{ detail.draft ? detail.draft.subject : 'No draft created' }}</h2>
          </div>
          <span v-if="detail.draft">To {{ detail.draft.to }}</span>
        </div>
        <pre class="draft-body">{{ detailLoading ? 'Loading draft…' : draftBody }}</pre>
        <div class="actions">
          <button class="secondary" :disabled="busy" @click="runAction('refresh')">Run Lynda again</button>
          <button class="danger" :disabled="busy" @click="runAction('wrong_answer')">Wrong answer</button>
          <button class="secondary" :disabled="busy" @click="runAction('done')">Done</button>
          <a class="secondary button-link" :href="selected.gmail_thread_url" target="_blank" rel="noopener">Open Gmail</a>
          <button
            class="primary"
            :disabled="busy || !canSend"
            @click="runAction('send')"
          >
            {{ busyAction === 'send' ? 'Working…' : sendLabel }}
          </button>
        </div>
      </article>

      <div v-if="!selected && !loading" class="no-selection">Select an email to review.</div>
    </section>
  </main>
</template>

<script>
import axios from 'axios'

const API_BASE = (process.env.VUE_APP_LYNDA_SUPPORT_API_BASE_URL || '').replace(/\/$/, '')

export default {
  name: 'SupportReview',
  data() {
    return {
      items: [],
      counts: { drafts: 0, needs_work: 0, done: 0 },
      queue: 'drafts',
      groupKey: '',
      search: '',
      selectedId: '',
      detail: {},
      loading: false,
      detailLoading: false,
      busyAction: '',
      error: '',
      tabs: [
        { key: 'drafts', label: 'Drafts' },
        { key: 'needs_work', label: 'Needs work' },
        { key: 'done', label: 'Done' },
      ],
    }
  },
  computed: {
    activeCount() {
      return (this.counts.drafts || 0) + (this.counts.needs_work || 0)
    },
    queueItems() {
      return this.items.filter(item => item.queue === this.queue && item.operator_state !== 'skipped')
    },
    groupOptions() {
      const groups = new Map()
      this.queueItems.forEach(item => {
        const key = item.group_key || 'other'
        const current = groups.get(key) || {
          key,
          label: [
            this.actionLabel(item.recommended_action),
            item.university_slug,
            item.ceremony_id,
          ].filter(Boolean).join(' · '),
          count: 0,
        }
        current.count += 1
        groups.set(key, current)
      })
      return Array.from(groups.values()).sort((a, b) => b.count - a.count).slice(0, 8)
    },
    filteredItems() {
      const needle = this.search.toLowerCase()
      return this.queueItems.filter(item => {
        if (this.groupKey && item.group_key !== this.groupKey) return false
        if (!needle) return true
        return [item.customer_name, item.customer_email, item.subject]
          .concat(item.registration_ids || []).join(' ').toLowerCase().includes(needle)
      })
    },
    selected() {
      return this.items.find(item => item.thread_id === this.selectedId) || null
    },
    busy() {
      return Boolean(this.busyAction)
    },
    registrationLabel() {
      const ids = (this.selected && this.selected.registration_ids) || []
      return ids.length ? ids.join(', ') : 'Not confirmed'
    },
    draftBody() {
      if (this.detail.draft) return this.detail.draft.body
      if (this.selected && this.selected.last_error) return this.selected.last_error
      return 'Lynda did not create a draft. Run the investigation again or open Gmail.'
    },
    retryingArchive() {
      return this.selected && this.selected.delivery_status === 'sent' && this.selected.archive_status === 'failed'
    },
    canSend() {
      return Boolean(this.detail.draft || this.retryingArchive)
    },
    sendLabel() {
      return this.retryingArchive ? 'Archive & next' : 'Send, archive & next'
    },
  },
  created() {
    this.loadQueue()
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    api(path = '') {
      if (!API_BASE) throw new Error('VUE_APP_LYNDA_SUPPORT_API_BASE_URL is not configured')
      return `${API_BASE}${path}`
    },
    async loadQueue(preferredId = '') {
      this.loading = true
      this.error = ''
      try {
        const response = await axios.get(this.api())
        this.items = response.data.items || []
        this.counts = response.data.counts || this.counts
        const nextId = preferredId || (this.filteredItems[0] && this.filteredItems[0].thread_id)
        await this.selectItem(nextId || '')
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.loading = false
      }
    },
    async selectItem(threadId) {
      if (!threadId) {
        this.selectedId = ''
        this.detail = {}
        return
      }
      this.selectedId = threadId
      this.detailLoading = true
      this.error = ''
      try {
        const response = await axios.get(this.api(`/${encodeURIComponent(threadId)}`))
        this.detail = response.data.item || {}
      } catch (error) {
        this.detail = {}
        this.error = this.errorMessage(error)
      } finally {
        this.detailLoading = false
      }
    },
    selectQueue(queue) {
      this.queue = queue
      this.groupKey = ''
      this.ensureSelection()
    },
    selectGroup(groupKey) {
      this.groupKey = groupKey
      this.ensureSelection()
    },
    ensureSelection() {
      this.$nextTick(() => {
        if (!this.filteredItems.some(item => item.thread_id === this.selectedId)) {
          this.selectItem(this.filteredItems[0] ? this.filteredItems[0].thread_id : '')
        }
      })
    },
    async runAction(action) {
      if (!this.selected || this.busy) return
      const currentIndex = this.filteredItems.findIndex(item => item.thread_id === this.selectedId)
      const next = this.filteredItems[currentIndex + 1] || this.filteredItems[currentIndex - 1]
      this.busyAction = action
      this.error = ''
      try {
        let actionWarning = ''
        const response = await axios.post(
          this.api(`/${encodeURIComponent(this.selectedId)}`),
          { action },
        )
        if (response.data.sent && !response.data.archived) {
          actionWarning = response.data.error
        }
        await this.loadQueue(next ? next.thread_id : '')
        if (actionWarning) this.error = actionWarning
      } catch (error) {
        this.error = this.errorMessage(error)
      } finally {
        this.busyAction = ''
      }
    },
    handleKeydown(event) {
      if (event.target && ['INPUT', 'TEXTAREA'].includes(event.target.tagName)) return
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault()
        this.runAction('send')
      }
      if (event.key.toLowerCase() === 'j') this.moveSelection(1)
      if (event.key.toLowerCase() === 'k') this.moveSelection(-1)
      if (event.key.toLowerCase() === 'g' && this.selected) window.open(this.selected.gmail_thread_url, '_blank', 'noopener')
    },
    moveSelection(direction) {
      const index = this.filteredItems.findIndex(item => item.thread_id === this.selectedId)
      const item = this.filteredItems[index + direction]
      if (item) this.selectItem(item.thread_id)
    },
    actionLabel(value) {
      return String(value || 'Other').replace(/_/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase())
    },
    linkLabel(value) {
      const labels = {
        thumbnails: 'Thumbnails',
        thumbnail_selection: 'Thumbnails',
        clip: 'Latest clip',
        latest_clip: 'Latest clip',
        registration: 'Registration',
      }
      return labels[value] || this.actionLabel(value || 'Open link')
    },
    errorMessage(error) {
      return (error.response && error.response.data && error.response.data.error) || error.message || 'Request failed'
    },
  },
}
</script>

<style scoped>
.support-review { max-width: 1600px; margin: 0 auto; padding: 24px; color: #172033; }
.review-header, .toolbar, .case-heading, .draft-heading, .actions { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.review-header h1, .case h2, .draft h2 { margin: 2px 0; }
.eyebrow { margin: 0; color: #64748b; font-size: 11px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; }
.health { color: #475569; font-size: 14px; }
.health-dot { display: inline-block; width: 9px; height: 9px; margin-right: 7px; border-radius: 50%; background: #16a34a; }
.health-dot.loading { background: #f59e0b; }
.toolbar { margin-top: 20px; justify-content: flex-start; }
.toolbar input { margin-left: auto; width: min(360px, 40vw); padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 8px; }
button, .button-link { border: 0; cursor: pointer; font: inherit; }
.tab, .groups button { padding: 9px 12px; border-radius: 8px; color: #475569; background: #f1f5f9; }
.tab.active, .groups button.active { color: #fff; background: #334155; }
.groups { display: flex; gap: 8px; margin: 14px 0; overflow-x: auto; }
.groups button { white-space: nowrap; font-size: 13px; }
.review-grid { display: grid; grid-template-columns: minmax(220px, .65fr) minmax(330px, 1fr) minmax(380px, 1.15fr); min-height: 660px; border: 1px solid #dbe3ec; border-radius: 12px; overflow: hidden; background: #fff; }
.queue { overflow-y: auto; border-right: 1px solid #e2e8f0; background: #f8fafc; }
.queue-item { display: block; width: 100%; padding: 14px; text-align: left; border-bottom: 1px solid #e2e8f0; background: transparent; }
.queue-item.selected { background: #e0e7ff; box-shadow: inset 3px 0 #4f46e5; }
.queue-name, .queue-subject, .queue-meta { display: block; }
.queue-name { font-weight: 700; }
.queue-subject { margin-top: 4px; overflow: hidden; color: #475569; font-size: 13px; text-overflow: ellipsis; white-space: nowrap; }
.queue-meta { margin-top: 8px; color: #64748b; font-size: 12px; }
.queue-meta em, .warning { float: right; padding: 3px 7px; border-radius: 999px; color: #92400e; background: #fef3c7; font-style: normal; }
.case, .draft { min-width: 0; padding: 20px; overflow-y: auto; }
.case { border-right: 1px solid #e2e8f0; }
.case-heading p, .draft-heading span { margin: 0; color: #64748b; font-size: 13px; }
.case-section { margin-top: 24px; }
.case-section h3 { margin-bottom: 8px; font-size: 13px; text-transform: uppercase; letter-spacing: .04em; }
pre { margin: 0; font: 14px/1.55 inherit; white-space: pre-wrap; overflow-wrap: anywhere; }
.case-section pre { max-height: 250px; padding: 12px; overflow-y: auto; border-radius: 8px; background: #f8fafc; }
dl { margin: 0; }
dl div { display: grid; grid-template-columns: 110px 1fr; gap: 10px; padding: 6px 0; }
dt { color: #64748b; }
dd { margin: 0; font-weight: 600; }
.reason { padding: 10px; border-left: 3px solid #818cf8; color: #475569; background: #eef2ff; }
.evidence-links { display: flex; flex-wrap: wrap; gap: 8px; }
.evidence-links a { padding: 7px 10px; border: 1px solid #cbd5e1; border-radius: 7px; color: #3730a3; text-decoration: none; }
.draft { display: flex; flex-direction: column; }
.draft-body { flex: 1; margin-top: 18px; padding: 18px; border: 1px solid #e2e8f0; border-radius: 9px; background: #fff; }
.actions { position: sticky; bottom: -20px; margin: 20px -20px -20px; padding: 14px 20px; justify-content: flex-end; border-top: 1px solid #e2e8f0; background: #fff; }
.primary, .secondary, .danger { padding: 10px 13px; border-radius: 8px; }
.primary { color: #fff; background: #4338ca; font-weight: 700; }
.primary:disabled { cursor: not-allowed; opacity: .5; }
.secondary { color: #334155; background: #e2e8f0; text-decoration: none; }
.danger { color: #991b1b; background: #fee2e2; }
.error-banner { padding: 10px 12px; border-radius: 8px; color: #991b1b; background: #fee2e2; }
.empty, .no-selection { padding: 24px; color: #64748b; }
@media (max-width: 1100px) {
  .review-grid { grid-template-columns: 240px 1fr; }
  .draft { grid-column: 2; border-top: 1px solid #e2e8f0; }
  .case { border-right: 0; }
}
@media (max-width: 720px) {
  .support-review { padding: 12px; }
  .review-grid { display: block; }
  .queue { max-height: 250px; border-right: 0; }
  .case { border-top: 1px solid #e2e8f0; }
  .toolbar { flex-wrap: wrap; }
  .toolbar input { width: 100%; margin-left: 0; }
}
</style>
